import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Spin } from '@sinohealth/butterfly-ui-components/lib';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getToken, removeToken } from '@/utils/cookies';
import ChangePassword from '@/pages/personal/changePassword';
import { SecurityProfile } from './api';
import { SecurityContext } from './context';
import { loadSecurityProfile } from './session';
import { readLoginState, updateLoginState, SECURITY_EVENT } from './storage';
import { requiredSecurityPath, SMS_PATH, PASSWORD_PATH } from './policy';
import SmsVerification from './SmsVerification';
import styles from './index.less';
import useIdleSession from './useIdleSession';
import { IDLE_TIMEOUT_MESSAGE } from './idleSession';

export default function SecurityGate({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [, rerender] = useState(0);
  const token = getToken() || '';
  const publicPage = location.pathname === '/login' || location.pathname === '/password';
  const [idleTimeoutNotice, setIdleTimeoutNotice] = useState(false);
  const [loaded, setLoaded] = useState<{ token: string; profile: SecurityProfile }>();
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const idleSessionActive = useIdleSession(token, !publicPage);

  useEffect(() => {
    if (location.pathname !== '/login') {
      setIdleTimeoutNotice(false);
      return;
    }
    const params = new URLSearchParams(location.search);
    if (params.get('reason') !== 'idle-timeout') return;
    setIdleTimeoutNotice(true);
    params.delete('reason');
    navigate(
      { pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  useEffect(() => {
    if (!idleTimeoutNotice) return undefined;
    const timer = window.setTimeout(() => setIdleTimeoutNotice(false), 3000);
    return () => window.clearTimeout(timer);
  }, [idleTimeoutNotice]);

  useEffect(() => {
    const reset = () => {
      setLoaded(undefined);
      setError('');
      rerender((n) => n + 1);
    };
    window.addEventListener(SECURITY_EVENT, reset);
    return () => window.removeEventListener(SECURITY_EVENT, reset);
  }, []);
  useEffect(() => {
    if (publicPage || !token || !idleSessionActive) return undefined;
    let active = true;
    setError('');
    loadSecurityProfile(token)
      .then((profile) => {
        if (active) setLoaded({ token, profile });
      })
      .catch((err) => {
        if (active) setError(err?.errMessage || err?.message || '无法获取登录安全配置，请重试');
      });
    return () => {
      active = false;
    };
  }, [token, publicPage, retry, idleSessionActive]);

  const profile = loaded?.token === token ? loaded.profile : undefined;
  const requiredPath = profile
    ? requiredSecurityPath(profile, !!readLoginState(token).smsVerified)
    : null;
  useEffect(() => {
    if (
      publicPage ||
      requiredPath !== PASSWORD_PATH ||
      location.pathname !== PASSWORD_PATH ||
      readLoginState(token).reminderSeen
    ) {
      return undefined;
    }
    const dialog = Modal.warning({
      title: '安全提醒',
      content: (
        <span>
          您的账号仍在使用<strong style={{ color: '#ff4d4f' }}>初始密码</strong>
          ，存在安全风险，请修改后继续使用系统。
        </span>
      ),
      okText: '立即修改密码',
      closable: false,
      maskClosable: false,
      keyboard: false,
      centered: true,
      onOk: () => {
        updateLoginState(token, { reminderSeen: true });
      },
    });
    return () => dialog.destroy();
  }, [token, requiredPath, publicPage, location.pathname]);

  const logout = () => {
    removeToken();
    navigate('/login', { replace: true });
  };
  const refreshProfile = useCallback(async () => {
    const latest = await loadSecurityProfile(token, true);
    if (getToken() === token) setLoaded({ token, profile: latest });
    return latest;
  }, [token]);
  const contextValue = useMemo(() => ({ profile, refreshProfile }), [profile, refreshProfile]);

  if (publicPage) {
    return (
      <>
        {location.pathname === '/login' && idleTimeoutNotice ? (
          <Alert
            className={styles.sessionNotice}
            message={IDLE_TIMEOUT_MESSAGE}
            type="warning"
            showIcon
            closable
            onClose={() => setIdleTimeoutNotice(false)}
          />
        ) : null}
        {children}
      </>
    );
  }
  if (!idleSessionActive) return null;
  if (!token) return <Navigate to="/login" replace />;
  if (!profile) {
    return (
      <div className={styles.loading}>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <Button onClick={() => setRetry((n) => n + 1)}>重试</Button>
            <Button onClick={logout}>返回登录</Button>
          </>
        ) : (
          <Spin tip="正在检查登录安全配置…" />
        )}
      </div>
    );
  }
  if (requiredPath && location.pathname !== requiredPath) {
    return <Navigate to={requiredPath} replace />;
  }
  if (requiredPath === SMS_PATH) {
    return (
      <SmsVerification
        key={token}
        token={token}
        expireSeconds={profile.smsCaptchaExpireSeconds}
        onLogout={logout}
        onVerified={() => {
          updateLoginState(token, { smsVerified: true });
          rerender((n) => n + 1);
          navigate(profile.pwdNeedChange ? PASSWORD_PATH : '/', { replace: true });
        }}
      />
    );
  }
  if (location.pathname === SMS_PATH) return <Navigate to="/" replace />;
  return (
    <SecurityContext.Provider value={contextValue}>
      {requiredPath === PASSWORD_PATH ? (
        <main className={styles.forcedPage}>
          <header>
            <strong>全病程管理服务平台</strong>
            <Button onClick={logout}>退出登录</Button>
          </header>
          <div className={styles.forcedBody}>
            <aside aria-label="个人中心导航">
              <button type="button" disabled>
                资料设置
              </button>
              <button type="button" disabled className={styles.selected}>
                修改密码
              </button>
              <button type="button" disabled>
                登录记录
              </button>
            </aside>
            <article>
              <p>个人中心 / 修改密码</p>
              <ChangePassword />
            </article>
          </div>
        </main>
      ) : (
        children
      )}
    </SecurityContext.Provider>
  );
}
